import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CoachMenuLayout from "../../../layouts/CoachMenuLayout";
import Loader from "../../../components/loader";
import { coachPaths } from "../../../routes/coach";
import { fetchCoachProfileData } from "../../../redux/features/coach/actions/coachAction";

const defaultProfileImage = "/assets/img/user_dummy_imag.avif";

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading2, coachProfile } = useSelector((state) => state.coach.coach);

  useEffect(() => {
    dispatch(fetchCoachProfileData());
  }, [dispatch]);

  const coach = coachProfile?.coach || {};
  const profileImage = coachProfile?.profile_image || defaultProfileImage;
  const headerImage = coachProfile?.profile_image || defaultProfileImage;
  const coachingTypes = parseList(coach?.coaching_type);
  const cvFiles = parseList(coach?.cv);
  const certificateFiles = parseList(coach?.certificate);
  const experiencesSource =
    coach?.experiences && coach?.experiences?.length
      ? coach?.experiences
      : coachProfile?.experiences;
  const experiences = parseList(experiencesSource);

  const formattedExperiences = useMemo(() => {
    return experiences.map((exp, index) => ({
      id: exp?.experience_id || index,
      job_title: exp?.job_title || "",
      company_name: exp?.company_name || "",
      start_date: exp?.start_date || "",
      end_date: exp?.end_date || "",
      currently_working: !!exp?.currently_working,
      job_description: exp?.job_description || "",
      link: exp?.link || "",
    }));
  }, [experiences]);

  if (isLoading2) {
    return <Loader />;
  }

  return (
    <CoachMenuLayout>
      <div className="ct_px_40 my-5">
        <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
          <div className="d-flex align-items-center gap-3 ct_flex_col_575">
            <div className="ct_profile_pic ct_flex_shrink_0">
              <img src={profileImage} alt="Coach" />
            </div>
            <div className="ct_flex_1">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <h4 className="ct_fs_20 ct_darkblue_text mb-2">
                    {coachProfile?.full_name || "Coach Name"}
                  </h4>
                  <p className="ct_fs_14 ct_darkblue_text mb-0">
                    {coach?.city || "City"} {coach?.country ? `, ${coach.country}` : ""}
                  </p>
                </div>
                <button
                  className="ct_yellow_btn ct_sky_blue_btn ct_h_32 ct_white_nowrap"
                  type="button"
                  onClick={() => navigate(coachPaths.ProfileEdit)}
                >
                  Edit Profile
                </button>
              </div>
              <p className="ct_para_scroll ct_custom_scroll ct_grey_text mt-3">
                {coach?.bio || "No bio added yet."}
              </p>
              <ul>
                <li className="d-flex align-items-center gap-2">
                  <img src="../assets/img/grey_mail_icon.svg" alt="" />
                  {coachProfile?.email || "N/A"}
                </li>
                <li className="d-flex align-items-center gap-2 mt-1">
                  <img src="../assets/img/grey_phon_icon.svg" alt="" />
                  {coachProfile?.phone_number || "N/A"}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-6 mb-4">
            <div className="ct_outline_bg ct_border_radius_10 ct_p_20">
              <h5 className="ct_fs_16 ct_darkblue_text mb-3">Coaching Details</h5>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Category</span>
                  <span className="ct_fs_14 ct_darkblue_text">
                    {coach?.category?.category_name || coach?.category_id || "N/A"}
                  </span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Format</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.format || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Tagline</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.tagline || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Experience</span>
                  <span className="ct_fs_14 ct_darkblue_text">
                    {coach?.year_of_experience || "N/A"} years
                  </span>
                </div>
                <div>
                  <p className="ct_fs_14 ct_grey_text mb-1">Coaching Approach</p>
                  <p className="ct_fs_14 ct_darkblue_text mb-0">
                    {coach?.coaching_approach || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="ct_fs_14 ct_grey_text mb-1">Coaching Type</p>
                  <p className="ct_fs_14 ct_darkblue_text mb-0">
                    {coachingTypes.length ? coachingTypes.join(", ") : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-6 mb-4">
            <div className="ct_outline_bg ct_border_radius_10 ct_p_20">
              <h5 className="ct_fs_16 ct_darkblue_text mb-3">Location</h5>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Country</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.country || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">State</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.state || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">City</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.city || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Address</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.address_1 || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">ZIP Code</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coach?.zip_code || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Time Zone</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coachProfile?.time_zone || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="ct_fs_14 ct_grey_text">Language</span>
                  <span className="ct_fs_14 ct_darkblue_text">{coachProfile?.language || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="ct_outline_bg ct_border_radius_10 ct_p_20">
              <h5 className="ct_fs_16 ct_darkblue_text mb-3">Experience</h5>
              {formattedExperiences.length ? (
                formattedExperiences.map((exp) => (
                  <div key={exp.id} className="ct_border_top_1_op_10 pt-3 mb-3">
                    <h6 className="ct_fs_14 ct_darkblue_text mb-1">
                      {exp.job_title} {exp.company_name ? `· ${exp.company_name}` : ""}
                    </h6>
                    <p className="ct_fs_12 ct_grey_text mb-1">
                      {exp.start_date || "N/A"} -{" "}
                      {exp.currently_working ? "Present" : exp.end_date || "N/A"}
                    </p>
                    <p className="ct_fs_12 ct_darkblue_text mb-0">
                      {exp.job_description || "N/A"}
                    </p>
                    {exp.link ? (
                      <a
                        className="ct_fs_12 ct_blue_text d-inline-block mt-1"
                        href={exp.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {exp.link}
                      </a>
                    ) : null}
                  </div>
                ))
              ) : (
                <p className="ct_fs_14 ct_grey_text mb-0">No experiences added yet.</p>
              )}
            </div>
          </div>
          <div className="col-12 mt-4">
            <div className="row">
              <div className="col-md-6 mb-4 mb-md-0">
                <div className="ct_outline_bg ct_border_radius_10 ct_p_20 h-100">
                  <h5 className="ct_fs_16 ct_darkblue_text mb-3">CV</h5>
                  {cvFiles.length ? (
                    <div className="d-flex flex-wrap gap-3 align-items-start">
                      {cvFiles.map((file, index) => (
                        <div
                          key={`${file}-${index}`}
                          style={{
                            width: "200px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <div className="ct_single_uploaded_img">
                            <img src={file} alt={`CV ${index + 1}`} />
                          </div>
                          <a
                            className="ct_yellow_btn ct_h_32 ct_white_nowrap mt-2"
                            style={{ width: "100%", textAlign: "center" }}
                            href={file}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View CV {index + 1}
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ct_fs_14 ct_grey_text mb-0">No CV uploaded yet.</p>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="ct_outline_bg ct_border_radius_10 ct_p_20 h-100">
                  <h5 className="ct_fs_16 ct_darkblue_text mb-3">Certificates</h5>
                  {certificateFiles.length ? (
                    <div className="d-flex flex-wrap gap-3 align-items-start">
                      {certificateFiles.map((file, index) => (
                        <div
                          key={`${file}-${index}`}
                          style={{
                            width: "200px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <div className="ct_single_uploaded_img">
                            <img src={file} alt={`Certificate ${index + 1}`} />
                          </div>
                          <a
                            className="ct_yellow_btn ct_h_32 ct_white_nowrap mt-2"
                            style={{ width: "100%", textAlign: "center" }}
                            href={file}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View Certificate {index + 1}
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ct_fs_14 ct_grey_text mb-0">No certificates uploaded yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CoachMenuLayout>
  );
};

export default Profile;
